import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, query, limit } from 'firebase/firestore';
import { db } from '../../firebase.js';
import useFirebaseCache from '../../hooks/useFirebaseCache.js';

const RecipeContext = createContext(null);

export const useRecipes = () => {
  const context = useContext(RecipeContext);
  if (!context) {
    throw new Error('useRecipes must be used within a RecipeProvider');
  }
  return context;
};

export const RecipeProvider = ({ children, user }) => {
  const [recipes, setRecipes] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [bookmarks, setBookmarks] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [userRecipes, setUserRecipes] = useState([]);
  
  const { 
    shouldRefreshCache, 
    hasCollectionChanged,
    getCachedData,
    setCachedData
  } = useFirebaseCache();

  // Load recipes from Firestore with caching
  const loadRecipes = useCallback(async () => {
    if (!user) {
      setRecipes([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    let cachedRecipes = getCachedData('cachedRecipes');
    const cachedTimestamp = parseInt(localStorage.getItem('cachedRecipes_timestamp') || '0', 10);
    
    // Check if we need to refresh the cache
    const needsRefresh = shouldRefreshCache('cachedRecipes', 60); // 1 hour cache
    
    try {
      // Only fetch from server if:
      // 1. We have no cached data OR
      // 2. Cache is expired AND collection has been updated since our last fetch
      const shouldFetch = !cachedRecipes.length || 
        (needsRefresh && await hasCollectionChanged('recipes', cachedTimestamp));
      
      // Load standard recipes
      let standardRecipes = [];
      if (!shouldFetch) {
        console.log('Using cached recipes - collection unchanged since last fetch');
        standardRecipes = cachedRecipes;
      } else {
        console.log('Fetching fresh recipes from Firestore');
        
        // Create a query that selects only the fields we need
        const q = query(
          collection(db, 'recipes'), 
          limit(200)
        );
        
        const querySnapshot = await getDocs(q);
        standardRecipes = querySnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          // Ensure ingredients is always an array
          ingredients: doc.data().ingredients || []
        }));
        
        setCachedData('cachedRecipes', standardRecipes);
      }

      // Load user recipes if user is logged in
      let userRecipes = [];
      if (user) {
        const cachedUserRecipes = getCachedData(`userRecipes_${user.uid}`);
        const userRecipesTimestamp = parseInt(localStorage.getItem(`userRecipes_${user.uid}_timestamp`) || '0', 10);
        const needsUserRefresh = shouldRefreshCache(`userRecipes_${user.uid}`, 5); // 5 minute cache
        
        const userShouldFetch = !cachedUserRecipes.length || 
          (needsUserRefresh && await hasCollectionChanged(`users/${user.uid}/recipes`, userRecipesTimestamp));
        
        if (!userShouldFetch) {
          console.log('Using cached user recipes - unchanged since last fetch');
          userRecipes = cachedUserRecipes;
        } else {
          console.log('Fetching fresh user recipes from Firestore');
          const userRecipesRef = collection(db, `users/${user.uid}/recipes`);
          const userRecipesQuery = query(userRecipesRef);
          const userQuerySnapshot = await getDocs(userRecipesQuery);
          
          userRecipes = userQuerySnapshot.docs.map(doc => {
            const recipeData = doc.data();
            
            return {
              id: doc.id,
              ...recipeData,
              origin: 'My Recipes'
            };
          });
          
          setCachedData(`userRecipes_${user.uid}`, userRecipes);
        }
      }

      // Combine standard and user recipes
      setRecipes([...standardRecipes, ...userRecipes]);
      setUserRecipes(userRecipes);
      setLoading(false);
    } catch (error) {
      console.error('Error loading recipes:', error);
      if (cachedRecipes.length) {
        setRecipes(cachedRecipes);
      }
      setLoading(false);
    }
  }, [user, getCachedData, hasCollectionChanged, setCachedData, shouldRefreshCache]);

  // Load favorites with caching
  const loadFavorites = useCallback(async () => {
    if (!user) {
      setFavorites(new Set());
      return;
    }
    
    const cachedFavorites = getCachedData(`userFavorites_${user.uid}`, []);
    const cachedTimestamp = parseInt(localStorage.getItem(`userFavorites_${user.uid}_timestamp`) || '0', 10);
    const needsRefresh = shouldRefreshCache(`userFavorites_${user.uid}`, 10); // 10 minute cache
    
    try {
      const shouldFetch = !cachedFavorites.length || 
        (needsRefresh && await hasCollectionChanged(`users/${user.uid}/favorites`, cachedTimestamp));
        
      if (!shouldFetch) {
        console.log('Using cached favorites - unchanged since last fetch');
        setFavorites(new Set(cachedFavorites));
        return;
      }

      console.log('Fetching fresh favorites');
      const favoritesQuery = query(collection(db, `users/${user.uid}/favorites`));
      const snapshot = await getDocs(favoritesQuery);
      const favoritesIds = snapshot.docs.map(doc => doc.data().recipeId || doc.id);
      
      setFavorites(new Set(favoritesIds));
      setCachedData(`userFavorites_${user.uid}`, [...favoritesIds]);
    } catch (error) {
      console.error('Error loading favorites:', error);
      if (cachedFavorites.length) {
        setFavorites(new Set(cachedFavorites));
      }
    }
  }, [user, getCachedData, hasCollectionChanged, setCachedData, shouldRefreshCache]);

  // Load bookmarks with caching
  const loadBookmarks = useCallback(async () => {
    if (!user) {
      setBookmarks(new Set());
      return;
    }
    
    const cachedBookmarks = getCachedData(`userBookmarks_${user.uid}`, []);
    const cachedTimestamp = parseInt(localStorage.getItem(`userBookmarks_${user.uid}_timestamp`) || '0', 10);
    const needsRefresh = shouldRefreshCache(`userBookmarks_${user.uid}`, 10); // 10 minute cache
    
    try {
      const shouldFetch = !cachedBookmarks.length || 
        (needsRefresh && await hasCollectionChanged(`users/${user.uid}/bookmarks`, cachedTimestamp));
        
      if (!shouldFetch) {
        console.log('Using cached bookmarks - unchanged since last fetch');
        setBookmarks(new Set(cachedBookmarks));
        return;
      }

      console.log('Fetching fresh bookmarks');
      const bookmarksQuery = query(collection(db, `users/${user.uid}/bookmarks`));
      const snapshot = await getDocs(bookmarksQuery);
      const bookmarkIds = snapshot.docs.map(doc => doc.data().recipeId || doc.id);
      
      setBookmarks(new Set(bookmarkIds));
      setCachedData(`userBookmarks_${user.uid}`, [...bookmarkIds]);
    } catch (error) {
      console.error('Error loading bookmarks:', error);
      if (cachedBookmarks.length) {
        setBookmarks(new Set(cachedBookmarks));
      }
    }
  }, [user, getCachedData, hasCollectionChanged, setCachedData, shouldRefreshCache]);

  // Toggle favorite status
  const toggleFavorite = useCallback(async (recipeId) => {
    if (!user) {
      console.warn('User not logged in. Please log in to save favorites.');
      return;
    }
    
    setFavorites(prevFavorites => {
      const newFavorites = new Set(prevFavorites);
      const isFavorite = newFavorites.has(recipeId);
      
      if (isFavorite) {
        newFavorites.delete(recipeId);
      } else {
        newFavorites.add(recipeId);
      }
      
      const favoriteRef = doc(db, `users/${user.uid}/favorites`, recipeId);
      if (!isFavorite) {
        setDoc(favoriteRef, { timestamp: new Date() }).catch(error => {
          console.error('Error saving favorite:', error);
          newFavorites.delete(recipeId);
          setFavorites(new Set(prevFavorites));
        });
      } else {
        deleteDoc(favoriteRef).catch(error => {
          console.error('Error removing favorite:', error);
          newFavorites.add(recipeId);
          setFavorites(new Set(prevFavorites));
        });
      }
      
      // Update cache
      setCachedData(`userFavorites_${user.uid}`, [...newFavorites]);
      return newFavorites;
    });
  }, [user, setCachedData]);

  // Toggle bookmark status
  const toggleBookmark = useCallback(async (recipeId) => {
    if (!user) {
      console.warn('User not logged in. Please log in to save bookmarks.');
      return;
    }
    
    setBookmarks(prevBookmarks => {
      const newBookmarks = new Set(prevBookmarks);
      const isBookmarked = newBookmarks.has(recipeId);
      
      if (isBookmarked) {
        newBookmarks.delete(recipeId);
      } else {
        newBookmarks.add(recipeId);
      }
      
      const bookmarkRef = doc(db, `users/${user.uid}/bookmarks`, recipeId);
      if (!isBookmarked) {
        setDoc(bookmarkRef, { timestamp: new Date() }).catch(error => {
          console.error('Error saving bookmark:', error);
          newBookmarks.delete(recipeId);
          setBookmarks(new Set(prevBookmarks));
        });
      } else {
        deleteDoc(bookmarkRef).catch(error => {
          console.error('Error removing bookmark:', error);
          newBookmarks.add(recipeId);
          setBookmarks(new Set(prevBookmarks));
        });
      }
      
      // Update cache
      setCachedData(`userBookmarks_${user.uid}`, [...newBookmarks]);
      return newBookmarks;
    });
  }, [user, setCachedData]);

  // Handle recipe added/updated/deleted
  const handleRecipeAdded = useCallback((newRecipe) => {
    setUserRecipes(prev => [...prev, newRecipe]);
    setRecipes(prev => [...prev, newRecipe]);
  }, []);

  const handleRecipeUpdated = useCallback((updatedRecipe) => {
    setRecipes(prev => prev.map(recipe => 
      recipe.id === updatedRecipe.id ? updatedRecipe : recipe
    ));
    setUserRecipes(prev => prev.map(recipe => 
      recipe.id === updatedRecipe.id ? updatedRecipe : recipe
    ));
  }, []);

  const handleRecipeDeleted = useCallback((deletedRecipeId) => {
    setUserRecipes(prev => prev.filter(recipe => recipe.id !== deletedRecipeId));
    setRecipes(prev => prev.filter(recipe => recipe.id !== deletedRecipeId));
  }, []);

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadRecipes();
      loadFavorites();
      loadBookmarks();
    } else {
      setRecipes([]);
      setFavorites(new Set());
      setBookmarks(new Set());
      setUserRecipes([]);
      setLoading(false);
    }
  }, [user, loadRecipes, loadFavorites, loadBookmarks]);

  const recipeContextValue = {
    recipes,
    userRecipes,
    favorites,
    bookmarks,
    loading,
    toggleFavorite,
    toggleBookmark,
    handleRecipeAdded,
    handleRecipeUpdated,
    handleRecipeDeleted,
    refreshRecipes: loadRecipes
  };

  return (
    <RecipeContext.Provider value={recipeContextValue}>
      {children}
    </RecipeContext.Provider>
  );
}; 